import * as THREE from "three";
import { isModuleToModuleConnection } from "../../utils/geometry.js";
import { isFreeStanding } from "../../utils/constants.js";
import { _Y_AXIS } from "./shared.js";

export const moduleDragMethods = {
  // Modules attached to a piece about to be dragged stay where they are; their references to
  // it would go stale as it moves. Children that are themselves selected travel with it.
  // The links are kept so a drag that is put back can restore them.
  _detachModuleChildren(furniture, selection = null) {
    for (const child of [...furniture.attachedChildren]) {
      if (child.furnitureData?.componentType !== "module") continue;
      if (selection?.includes(child)) continue;
      this.detachedModuleLinks.push({ child, ap: child.attachedTo, side: child.moduleConnectionSide ?? null });
      child.detachFrom(this.app.getScene());
    }
  },

  /**
   * Put every dragged piece back where the drag began and re-bind the links the drag cut —
   * the piece's own, and those of module children it left behind.
   */
  _restoreDraggedModules() {
    for (const { furniture, position, originalAttachmentPoint, originalChildAttachmentSide } of this.initialDragPositions) {
      furniture.setPosition(position.x, position.y, position.z);
      if (this.initialDragPositions.length === 1 && this.dragSelectRotation != null) {
        furniture.getMesh().quaternion.setFromAxisAngle(_Y_AXIS, this.dragSelectRotation);
      }
      furniture.getMesh().visible = true;
      if (originalAttachmentPoint && !originalAttachmentPoint.isOccupied) {
        furniture.attachTo(originalAttachmentPoint, originalChildAttachmentSide || null);
      }
      furniture.calculateBoundingBox();
    }
    for (const { child, ap, side } of this.detachedModuleLinks) {
      if (ap && !ap.isOccupied) child.attachTo(ap, side);
    }
  },

  /**
   * Create the ghost that previews the dragged piece — at a snap (green) or where it can't
   * stay (red). One per drag: it is hidden in between, not rebuilt every frame.
   * @param {FurnitureElement} furniture - The furniture being dragged
   */
  async createDragGhost(furniture) {
    if (!furniture || !furniture.furnitureData) return;
    if (this.isDragGhostActive || this._dragGhostPending) return;

    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return;

    this._dragGhostPending = true;
    try {
      // Clone from the live mesh so the snap preview matches the dragged piece's
      // current pose (active variant + in-progress variant transitions) instead
      // of falling back to the GLTF rest pose.
      await furnitureManager.createGhostFurniture(furniture.furnitureData, furniture);
    } finally {
      this._dragGhostPending = false;
    }
    this.dragGhostFurniture = furnitureManager.getGhostFurniture();
    // Only the drag ghost casts a shadow, so the moving piece stays grounded
    // while its real mesh is hidden. (Catalog-spawn ghosts stay shadowless.)
    this.dragGhostFurniture?.setCastShadow(true);
    this.isDragGhostActive = true;
  },

  /**
   * Update drag ghost position and state
   * @param {THREE.Vector3} position - The position to place the ghost
   * @param {number} rotation - The rotation angle
   * @param {boolean} isSnapped - Whether the ghost is snapped
   * @param {object} attachmentPoint - The attachment point being snapped to
   * @param {string} childAttachmentSide - The child attachment side being used
   */
  updateDragGhost(position, rotation, isSnapped, attachmentPoint = null, childAttachmentSide = null, isValid = true) {
    if (!this.dragGhostFurniture) return;

    this.dragGhostFurniture.setPosition(position.x, position.y, position.z);
    this.dragGhostFurniture.setRotation(rotation);
    this.dragGhostFurniture.setSnapped(isSnapped, attachmentPoint, childAttachmentSide, isValid);
    this.dragGhostFurniture.show();
  },

  /**
   * Clear the drag ghost furniture
   */
  clearDragGhost() {
    if (this.isDragGhostActive && this.dragGhostFurniture) {
      const furnitureManager = this.app.getFurnitureManager();
      if (furnitureManager) {
        furnitureManager.clearGhostFurniture();
      }
      this.dragGhostFurniture = null;
      this.isDragGhostActive = false;
    }
  },

  _beginDrag() {
    this.detachedModuleLinks = [];
    // For multi-select: detach module-to-module connections ONLY if the connected module is NOT in the selection
    const selectedFurnitureArray = this.app.getFurnitureManager()?.getSelectedFurnitureArray() || [];
    if (this.initialDragPositions.length > 0 && selectedFurnitureArray.length > 1) {
      this.initialDragPositions.forEach(({ furniture }) => {
        // Detach from parent if outside the selection
        if (furniture.attachedTo) {
          const parentFurniture = furniture.attachedTo.parentFurniture;
          const isModuleConnection = isModuleToModuleConnection(furniture, parentFurniture);
          const parentInSelection = selectedFurnitureArray.includes(parentFurniture);

          if (isModuleConnection && !parentInSelection) {
            furniture.detachFrom(this.app.getScene());
          }
        }

        this._detachModuleChildren(furniture, selectedFurnitureArray);
      });
    } else {
      // Single selection
      const selectedFurniture = this.app.getFurnitureManager()?.getSelectedFurniture();

      if (selectedFurniture && selectedFurniture.attachedTo) {
        const parentFurniture = selectedFurniture.attachedTo.parentFurniture;
        const isComponent = selectedFurniture.furnitureData?.componentType &&
                            selectedFurniture.furnitureData.componentType !== 'module';

        if (isComponent) {
          this._beginComponentDrag(selectedFurniture);
        } else if (isModuleToModuleConnection(selectedFurniture, parentFurniture)) {
          // Module-to-module: save attachment info BEFORE detaching so we can restore on invalid snap
          const originalAttachmentPoint = selectedFurniture.attachedTo;
          const childAP = selectedFurniture.attachmentPoints.find(
            ap => ap.isOccupied && ap.attachedFurniture === originalAttachmentPoint.parentFurniture
          );
          const originalChildAttachmentSide = childAP?.side ?? null;

          selectedFurniture.detachFrom(this.app.getScene());

          this.initialDragPositions = [{
            furniture: selectedFurniture,
            position: selectedFurniture.getMesh().position.clone(),
            originalAttachmentPoint,
            originalChildAttachmentSide,
          }];
        }
      }

      if (selectedFurniture && !this.componentDragOriginalAP) {
        this._detachModuleChildren(selectedFurniture);

        // Capture world position after potential detach so we can restore it on invalid snap
        if (!this.initialDragPositions.length) {
          this.initialDragPositions = [{
            furniture: selectedFurniture,
            position: selectedFurniture.getMesh().position.clone(),
          }];
        }
      }
    }
  },

  _moveMultiSelection(raycaster, furnitureManager) {
    const groundIntersects = this._intersectGround(raycaster);

    if (groundIntersects.length > 0 && this.dragStartPosition) {
      const currentPosition = groundIntersects[0].point;
      const delta = this._dragDelta.subVectors(currentPosition, this.dragStartPosition);

      // Clamp delta for the entire group to preserve relative positions (room bounds)
      const clampedDelta = this.clampDeltaForGroup(this.initialDragPositions, delta);

      // Build the exclude set: all furniture in the moving group
      const groupExcludeSet = new Set(this.initialDragPositions.map(({ furniture }) => furniture));

      // Check if moving by clampedDelta causes any inter-module collision
      const groupHasCollision = this.initialDragPositions.some(({ furniture, position }) => {
        if (furniture.furnitureData?.componentType !== "module") return false;
        const testPos = this._dragTestPos.copy(position).add(clampedDelta);
        return this.checkFurnitureCollisions(furniture, testPos, groupExcludeSet);
      });

      // On collision hold at last valid delta — prevents jump back to drag-start position
      let resolvedDelta;
      if (groupHasCollision) {
        resolvedDelta = this.lastValidGroupDelta ?? new THREE.Vector3(0, clampedDelta.y, 0);
      } else {
        resolvedDelta = clampedDelta;
        this.lastValidGroupDelta = (this.lastValidGroupDelta ?? new THREE.Vector3()).copy(clampedDelta);
      }

      // Apply the same resolved delta to all selected items
      this.initialDragPositions.forEach(({ furniture, position }) => {
        furniture.setPosition(position.x + resolvedDelta.x, position.y + resolvedDelta.y, position.z + resolvedDelta.z);
      });

      furnitureManager.updateSelectionOutline();
    }
  },

  _moveModule(selectedFurniture, raycaster, furnitureManager) {
    const groundIntersects = this._intersectGround(raycaster);

    if (groundIntersects.length > 0) {
      // Apply drag offset so the item doesn't jump to the cursor ray's ground hit.
      // In 3D perspective the cursor hits the ground at a point that may not match the
      // furniture's center — we preserve the XZ offset captured at mousedown.
      let newPosition;
      if (this.dragStartPosition && this.initialDragPositions.length > 0) {
        const delta = this._dragDelta.subVectors(groundIntersects[0].point, this.dragStartPosition);
        newPosition = this._dragPosition.copy(this.initialDragPositions[0].position).add(delta);
        newPosition.y = this.initialDragPositions[0].position.y;
      } else {
        newPosition = groundIntersects[0].point;
      }

      // Check if this is a module that can snap to attachment points
      const isModule = selectedFurniture.furnitureData?.componentType === "module";
      let snappedToAttachment = false;

      // Start creating the drag ghost (async); it is shown once it's ready.
      this.createDragGhost(selectedFurniture);

      // Snap SELECTION uses the rotation captured at drag start, NOT the live mesh
      // rotation. The mesh is auto-rotated each frame to fit the snapped face (below);
      // feeding that back here makes the chosen face oscillate between orientations.
      // Capture lazily on the first drag frame, before any auto-rotate runs.
      // (Read via 'YXZ' Euler — 'XYZ' hits gimbal lock at 180° and reads y≈0.)
      if (this.dragSelectRotation == null) {
        this.dragSelectRotation = new THREE.Euler().setFromQuaternion(
          selectedFurniture.getMesh().quaternion, 'YXZ'
        ).y;
      }

      if (isModule) {
        const currentRotation = this.dragSelectRotation;
        const draggedAttachmentPointsForSnap = this._serializeModuleAPs(selectedFurniture);
        const specialCaseSnapping = selectedFurniture.furnitureData?.properties?.specialCaseSnapping === true;
        const closestAttachment = this.findClosestModuleAttachment(newPosition, selectedFurniture, draggedAttachmentPointsForSnap, currentRotation, specialCaseSnapping);

        if (closestAttachment) {
          // Clear previous highlight if we switched to a different attachment point
          if (this.previousDragSnapTarget && this.previousDragSnapTarget !== closestAttachment) {
            this.previousDragSnapTarget.setHighlight(false);
          }

          // Calculate snapped position. Pass the live drag pose (newPosition) so the
          // connector-distance tiebreak matches the ranking used to pick this target.
          const snapResult = this.calculateSnapPosition(closestAttachment, draggedAttachmentPointsForSnap, currentRotation, newPosition);

          if (snapResult) {
            // Validity = within room bounds AND no collision with other modules.
            //
            // Exclude the parent's ENTIRE connected group (not just the direct parent).
            // This matches designer-unity's CanFitInPosition which uses exact physics:
            // face-to-face connections never produce overlap, so the parent group's
            // modules don't interfere. With AABB we can't be that precise — a long
            // module (e.g. E3) would AABB-overlap modules on the other side of an R
            // corner piece even though the physical meshes don't touch.
            //
            // The "B-already-there" case (snapping onto an occupied face) is already
            // prevented by AP isOccupied state — that face is never offered as a snap
            // target, so we don't need collision detection to catch it.
            const snapBoundsValid = this.isSnapPositionInBounds(selectedFurniture, snapResult.position);
            const snapExclude = new Set([
              ...closestAttachment.parentFurniture.getAllGroupModules(),
              ...selectedFurniture.getAllGroupModules(),
            ]);
            // Pass snapResult.rotation so the AABB is computed at the snap destination
            // rotation, not the current drag rotation. This is the correct check — a module
            // rotated 90° at snap has different width/depth than it does mid-drag.
            const snapHasCollision = this.checkFurnitureCollisions(selectedFurniture, snapResult.position, snapExclude, snapResult.rotation);
            const snapIsValid = snapBoundsValid && !snapHasCollision &&
              !this._snapPenetratesGroup(selectedFurniture, snapResult, closestAttachment);
            if (snapIsValid) {
              // Only snap when valid — invalid snaps fall through to the normal drag
              // path so the furniture follows the cursor and renders red there instead
              // of being locked to an invalid (red) ghost at the snap position.
              snappedToAttachment = true;
              this.dragSnapIsInvalid = false;
              this.dragSnapTarget = closestAttachment;
              this.previousDragSnapTarget = closestAttachment;

              closestAttachment.setHighlight(true, true);

              // Update drag ghost to show snapped position (if ghost is ready)
              if (this.dragGhostFurniture) {
                this.updateDragGhost(
                  snapResult.position,
                  snapResult.rotation,
                  true, // isSnapped
                  closestAttachment,
                  snapResult.childAttachmentSide,
                  true
                );

                // Hide the actual furniture while showing ghost
                selectedFurniture.getMesh().visible = false;
              } else {
                // Ghost not ready yet, just move the actual furniture
                selectedFurniture.getMesh().visible = true;
              }

              selectedFurniture.setPosition(snapResult.position.x, snapResult.position.y, snapResult.position.z);
              // Use quaternion to avoid corrupting Euler x/z components at 180° (gimbal lock)
              selectedFurniture.getMesh().quaternion.setFromAxisAngle(_Y_AXIS, snapResult.rotation);
              this.dragSnapChildAttachmentSide = snapResult.childAttachmentSide;
            }
            // Invalid snap: fall through — non-snapped branch handles cursor position + red highlight
          }
        }
      }

      if (!snappedToAttachment) {
        // Clear any previous highlight
        if (this.previousDragSnapTarget) {
          this.previousDragSnapTarget.setHighlight(false);
          this.previousDragSnapTarget = null;
        }

        // Normal ground drag - no snapping, clamped to room bounds
        this.dragSnapTarget = null;
        this.dragSnapChildAttachmentSide = null;
        // Revert to the intended orientation in free space — a prior snap frame may
        // have auto-rotated the mesh. Selection already uses dragSelectRotation; this
        // keeps the visible piece consistent with it. (Guarded: only set for drags
        // where a rotation was captured, i.e. modules.)
        if (this.dragSelectRotation != null) {
          selectedFurniture.getMesh().quaternion.setFromAxisAngle(_Y_AXIS, this.dragSelectRotation);
        }
        // The piece follows the cursor even where it can't stay, shown as a red ghost, and a
        // drop there puts it back (Unity's CheckValidity). Holding it at its last free spot
        // instead left it behind — often at the snap it just left — as if dropped mid-drag.
        const position = this.clampPositionToRoomBounds(selectedFurniture, newPosition);
        selectedFurniture.setPosition(position.x, position.y, position.z);
        this.dragSnapIsInvalid = isFreeStanding(selectedFurniture) &&
          this.checkFurnitureCollisions(selectedFurniture, position, selectedFurniture.getAllGroupModules());

        const mesh = selectedFurniture.getMesh();
        if (this.dragSnapIsInvalid && this.dragGhostFurniture) {
          this.updateDragGhost(position, this.dragSelectRotation, false, null, null, false);
          mesh.visible = false;
        } else {
          this.dragGhostFurniture?.hide();
          mesh.visible = true;
        }
      }

      furnitureManager.updateSelectionOutline();
    }
  },

  _dropModule(selectedFurniture) {
    // Make sure furniture is visible again after drag
    if (selectedFurniture && selectedFurniture.getMesh()) {
      selectedFurniture.getMesh().visible = true;
    }

    // Clear drag ghost if active
    this.clearDragGhost();

    if (this.isDragging && selectedFurniture && this.dragSnapIsInvalid) {
      // Released where it can't stay — back to where the drag began, re-linked.
      this._restoreDraggedModules();
      this.app.notifyStateChanged();
    } else if (this.isDragging && this.dragSnapTarget) {
      // Snapped to an attachment point: attach the furniture if that AP is still free.
      if (selectedFurniture && !this.dragSnapTarget.isOccupied) {
        // Detach from current attachment if already attached
        if (selectedFurniture.attachedTo) {
          selectedFurniture.detachFrom(this.app.getScene());
        }

        // Attach to the new attachment point, passing the child's connection side
        // so hasAdjacentModuleOnSide knows which panel APs to hide immediately.
        // attachTo also marks all APs on both the child's and parent's connection
        // faces as occupied (group fix — prevents a second module snapping to
        // an already-connected face).
        selectedFurniture.attachTo(this.dragSnapTarget, this.dragSnapChildAttachmentSide || null);

        selectedFurniture.calculateBoundingBox();
      }

      // Trigger save after drag operation completes
      this.app.notifyStateChanged();
    }

    // Reset invalid-snap flag
    this.dragSnapIsInvalid = false;

    // Clear ALL attachment point highlights to ensure clean state
    this.clearAllAttachmentHighlights();

    if (this.isDragging) {
      this.isDragging = false;
      this.justFinishedDragging = true; // Mark that we just finished dragging to prevent click

      // Also trigger save if furniture was dragged (even if not snapped)
      this.app.notifyStateChanged();

      // After the snap branch, so the piece is final before the group re-centres.
      this.app.getFurnitureManager()?.centerIfAuto();
    }

    // Refresh debug AP overlay colors if active
    this.app.getFurnitureManager()?._debugRefreshModuleAPs?.();

    // Refresh selection UI (rotate button enabled state, etc.) after drag — attachment
    // state may have changed (detach on drag start, attach/re-attach on drop).
    if (selectedFurniture) {
      this.app.getUIManager()?.updateSelection(
        this.app.getFurnitureManager()?.getSelectedFurnitureArray() || []
      );
    }

    // After any module drag completes, check all modules for cushions that lost
    // their required adjacent module support (fragile cushion removal).
    // Done post-move (not at drag start) so that restoring to original position
    // on invalid snap doesn't needlessly remove cushions.
    if (this.initialDragPositions.length > 0) {
      const fm = this.app.getFurnitureManager();
      if (fm) {
        const allModules = fm.getAllFurniture().filter(
          f => f.furnitureData?.componentType === 'module'
        );
        fm.removeUnsupportedFragileCushions(allModules);
      }
    }

    // Reset dragging state
    this.isDraggingValid = false;
    this.mouseDownPosition = null;
    this.initialDragPositions = [];
    this.detachedModuleLinks = [];
    this.dragStartPosition = null;
    this.dragSnapChildAttachmentSide = null;
    this.dragSelectRotation = null;
  },
};
